<script setup lang="ts">
    import { onMounted, ref, reactive, computed } from 'vue';
    import { IDocumentUnsolicitedQueueResponse, IUnsolicitedDocumentSaveRequest, IUnsolicitedDocumentAuditResponse,
        IUnsolicitedDocumentSetupApiKeyListResponse, IUnsolicitedDocumentSetupReviewerListResponse } from '../../lib/Models/DocumentModel';
    import * as DateUtils from '../../lib/DateUtils';
    import * as UIEnums from '../../Enums/UiEnums';
    import * as ChoiceEnums from '../../Enums/ChoiceEnums';
    import SectionPanel from '../../components/SectionPanel.vue';
    import PrettyFileSize from '../../components/PrettyFileSize.vue';
    import { ISearchPatientItem, ISearchResourceItem, ISearchPayerItem, ISearchDocumentFolderItem,  
        IListByNameExtended, ISearchPatientKey, ISearchDocumentTypeItem, ISearchDocumentTypeKey, ISearchDocumentFolderKey } from '../../lib/Models/SearchModel';
    import * as Validation from "../../lib/Validation";
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import TextInput from '../../components/TextInput.vue'
    import Icon from '../../components/Icon.vue'
    import ActionButton from "../../components/ActionButton.vue"; 
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import SearchUserGroupMultiple from '../../components/Search/SearchUserGroupMultiple.vue'
    import SearchPatient from '../../components/Search/SearchPatient.vue'
    import SearchResource from '../../components/Search/SearchResource.vue'
    import SearchPayer from '../../components/Search/SearchPayer.vue'
    import SearchDocumentFolder from '../../components/Search/SearchDocumentFolder.vue'
    import SearchDocumentType from '../../components/Search/SearchDocumentType.vue';
    import ModalDialog from '../../components/ModalDialog.vue'
    import PatientCreateQuick from "../../ChoiceScreens/Screens/PatientCreateQuick.vue";

    import { documents } from '../../lib/ChoiceDocumentApi';
    import PageTitle from '../../components/PageTitle.vue';
    import MessageSection from '../../components/Messages/MessageSection.vue';
    import UnsolicitedDocumentAudit from '../UnsolicitedDocuments/UnsolicitedDocumentAudit.vue';   
    import { user } from '../../lib/ChoiceApi';

    interface ISearchItem {
        searchResultItem?: ISearchPatientItem | ISearchResourceItem | ISearchPayerItem,
        folderItem?: ISearchDocumentFolderItem,
        displaySearchError: boolean,
        displayFolderError: boolean
    }

    const props = withDefaults(defineProps<{
        documentId?: number,
        canUpdate: number,
        authUser: string,
        viewAudit: number
    }>(), {
    });

    onMounted(async () => {    
        const setupApiKeyListPromise = loadApiKeyList();
        const setupReviewerPromise = loadApiKeyReviewerList();
        const documentPromise = loadDocument(props.documentId ?? 0);
        const documentReviewerPromise = loadDocumentReviewers(props.documentId ?? 0);
        const auditPromise = loadAudit();        
        await Promise.all([setupApiKeyListPromise, setupReviewerPromise, documentPromise, documentReviewerPromise, auditPromise]); 

        //pre-populate the form with values from selected unsolicited document
        const fileExtStart = selectedDocument.documentInfo?.documentFileName.indexOf(".");    
        formData.filename = !selectedDocument.documentInfo?.documentFileName ? firstApiKeyWithSetupData.value?.fileNameDefault : selectedDocument.documentInfo?.documentFileName.slice(0,fileExtStart);
        formData.receivedDate = DateUtils.toDisplayDateString(selectedDocument.documentInfo?.dateCreated, UIEnums.DateDisplayFormat.NumericDate); 
        defaultPatientKey.value = {patientId: selectedDocument.documentInfo?.identifierId};
        defaultDocumentTypeKey.value = !selectedDocument.documentInfo?.documentTypeId ? {documentTypeId: firstApiKeyWithSetupData.value?.documentTypeIdDefault} : {documentTypeId: selectedDocument.documentInfo?.documentTypeId};
        defaultDocumentFolderKey.value = !selectedDocument.documentInfo?.officeMenuId ? {menuId: firstApiKeyWithSetupData.value?.saveFolderIdDefault} : {menuId: selectedDocument.documentInfo?.officeMenuId};
               
        if (selectedReviewerData.reviewerList) {
            for (const item of selectedReviewerData.reviewerList) {
                let reviewerInfo = item.reviewerType == 2 ? await user.GetUserGroupInfo(item.reviewerId) : await user.SimpleInfoByIdType(ChoiceEnums.DatabaseIdTypes.security_users_id, item.reviewerId?.toString());
                let reviewerDescNew = item.reviewerType == 2 ? reviewerInfo.description : reviewerInfo.user_name;

                formData.reviewList?.push({
                    identifier: item.reviewerId?.toString() ?? "",
                    optionalIdentifier: item.reviewerType?.toString(),
                    display: reviewerDescNew,
                    optionalDisplay: item.reviewerTypeId == 2 ? "Group" : "User",
                    selected: true,
                    header: false
                });
            }
        }

        if (firstApiKeyReviewerSetupData.value && (selectedReviewerData.reviewerList?.length ?? 0) < 1) {
            for (const item of firstApiKeyReviewerSetupData.value) {                
                formData.reviewList?.push({
                    identifier: item.reviewerId?.toString() ?? "",
                    optionalIdentifier: item.reviewerTypeId?.toString(),
                    display: item.reviewerDesc ?? "",
                    optionalDisplay: item.reviewerTypeId == 2 ? "Group" : "User",
                    selected: true,
                    header: false
                });
            }
        }
               
        formData.priority = selectedDocument.documentInfo?.priority ?? ChoiceEnums.Priority.Normal;
        formData.reviewMessage = selectedDocument.documentInfo?.reviewMessage;
        formData.menuNameId = getMenuNameTypeValue(selectedDocument.documentInfo?.identifierType) ?? ChoiceEnums.MenuNameType.Patient;
        formData.isReconciled = selectedDocument.documentInfo?.isReconciled ?? false;
        formData.dateReconciled = DateUtils.toDisplayDateString(selectedDocument.documentInfo?.dateReconciled, UIEnums.DateDisplayFormat.NumericDate);
    });
 
    const selectedDocument = reactive<{
        documentInfo?: IDocumentUnsolicitedQueueResponse
    }>({});

    const selectedDocumentAudit = reactive<{
        auditData?: IUnsolicitedDocumentAuditResponse
    }>({});

    const apiKeyData = reactive<{
        apiKeyList?: IUnsolicitedDocumentSetupApiKeyListResponse[]
    }>({});

    const reviewerData = reactive<{
        reviewerList?: IUnsolicitedDocumentSetupReviewerListResponse[]
    }>({});

    const selectedReviewerData = reactive<{
        reviewerList?: IUnsolicitedDocumentSetupReviewerListResponse[]
    }>({});

    const firstApiKeyWithSetupData = computed(() => {
        return apiKeyData.apiKeyList ? apiKeyData.apiKeyList[0] : undefined;
    });

    const firstApiKeyReviewerSetupData = computed(() => {
        return reviewerData.reviewerList ? reviewerData.reviewerList?.filter(item => item.unsolicitedDocumentSetupId == firstApiKeyWithSetupData.value?.unsolicitedDocumentSetupId) : undefined;
    });

    function getMenuNameTypeValue(name: string | undefined): ChoiceEnums.MenuNameType {
        const enumValues = Object.values(ChoiceEnums.MenuNameType);

        for (const value of enumValues) {
            if (typeof value === "number") {
                if (ChoiceEnums.MenuNameType[value] === name) {
                        return value;
                    }
            }
        }

        return 1;  //default to patient if undefined
    }

    const IMAGE_ID_NOT_SAVED = 0;
    let pageTitle = "Unsolicited Document Reconciliation ";
    pageTitle += props.viewAudit ? "Audit" : "";

    const formData = reactive<{
        display: boolean,
        menuNameId: ChoiceEnums.MenuNameType,
        filename?: string,
        receivedDate?: DateUtils.NoTzDate,
        reviewList: IListByNameExtended[],
        reviewMessage?: string,
        priority?: ChoiceEnums.Priority,
        saving: boolean,
        savingNext: boolean,
        errorMessage?: string,
        imageId?: number,
        createNewPatient: boolean,
        useGroupName: boolean,
        nextSave: boolean,
        createPatientPrivilege: ChoiceEnums.PrivilegeType,
        isReconciled: boolean,
        dateReconciled?: DateUtils.NoTzDate
    }>({
        display: false,
        menuNameId: ChoiceEnums.MenuNameType.Patient,
        reviewList: [],
        priority: ChoiceEnums.Priority.Normal,
        saving: false,
        savingNext: false,
        imageId: IMAGE_ID_NOT_SAVED,
        createNewPatient: false,
        useGroupName: false,
        nextSave: false,
        createPatientPrivilege: ChoiceEnums.PrivilegeType.None,
        isReconciled: false
    });

    const displayPatientMenu = computed<boolean>(() => formData.menuNameId == ChoiceEnums.MenuNameType.Patient);
    const displayPracticeMenu = computed<boolean>(() => formData.menuNameId == ChoiceEnums.MenuNameType.Practice);
    const displayResourceMenu = computed<boolean>(() => formData.menuNameId == ChoiceEnums.MenuNameType.Resource);
    const displayPayerMenu = computed<boolean>(() => formData.menuNameId == ChoiceEnums.MenuNameType.Payer);
    const defaultPatientKey = ref<ISearchPatientKey>();
    const defaultDocumentTypeKey = ref<ISearchDocumentTypeKey>();
    const defaultDocumentFolderKey = ref<ISearchDocumentFolderKey>();

    const isDocumentTypeRequired = computed<boolean>(() => !firstApiKeyWithSetupData.value ? false : firstApiKeyWithSetupData.value?.documentTypeRequired);
    const isReviewRequired = computed<boolean>(() => !firstApiKeyWithSetupData.value ? false : firstApiKeyWithSetupData.value?.documentReviewRequired);
    const isFolderRequired = computed<boolean>(() => !firstApiKeyWithSetupData.value ? false : firstApiKeyWithSetupData.value?.saveFolderRequired);

    const disableSaveButton = computed<boolean>(() => (formData.saving || 
                                                        undefined != formData.errorMessage || 
                                                        !formData.filename || 
                                                        (displayPatientMenu.value && !searchItem.value[ChoiceEnums.MenuNameType.Patient].searchResultItem?.entity_id) ||
                                                        (displayResourceMenu.value && !searchItem.value[ChoiceEnums.MenuNameType.Resource].searchResultItem?.entity_id) ||
                                                        (displayPayerMenu.value && !searchItem.value[ChoiceEnums.MenuNameType.Payer].searchResultItem?.entity_id) ||
                                                        (isFolderRequired.value && !searchItem.value[formData.menuNameId].folderItem) ||
                                                        (isDocumentTypeRequired.value && !searchItemDocumentType.value) ||
                                                        (isReviewRequired.value && formData.reviewList.length < 1) ||
                                                        1 !== props.canUpdate) ? true : false);

    const searchItem = ref<ISearchItem[]>([
        {displaySearchError: false, displayFolderError: false},
        {displaySearchError: false, displayFolderError: false},
        {displaySearchError: false, displayFolderError: false},
        {displaySearchError: false, displayFolderError: false}
    ]);

    const searchItemDocumentType = ref<ISearchDocumentTypeItem>();

    const createNewPatientEnabled = computed<boolean>(() => formData.createPatientPrivilege == ChoiceEnums.PrivilegeType.ReadUpdateWrite ? true : false);
    const displayFilenameError = computed<boolean>(() => (formData?.filename?.length ?? 0) > 0 ? false : true);

    async function loadDocument(documentId: number) {
        const resp = await documents.getDocument(documentId);
        selectedDocument.documentInfo = resp;
    }

    async function loadDocumentReviewers(documentId: number) {
        const resp = await documents.getDocumentReviewers(documentId);
        selectedReviewerData.reviewerList = resp;
    }

    async function loadAudit() {
        if (!props.documentId) {
            return;
        }
        const resp = await documents.auditDocument(props.documentId);
        selectedDocumentAudit.auditData = resp;
    }

    async function loadApiKeyList() {
        const resp = await documents.getApiKeyDocumentSetupList();
        apiKeyData.apiKeyList = resp;
    }

    async function loadApiKeyReviewerList() {
        const resp = await documents.getApiKeyDocumentSetupReviewerList();
        reviewerData.reviewerList = resp;
    }


    function onChangeSearch(searchResultItem: ISearchPatientItem | ISearchResourceItem | ISearchPayerItem, menuNameType: ChoiceEnums.MenuNameType) {
        searchItem.value[menuNameType].searchResultItem = searchResultItem;
        searchItem.value[menuNameType].displaySearchError = !searchItem.value[menuNameType].searchResultItem ? true : false; 
    }

    function onChangeFolder(folder: ISearchDocumentFolderItem, menuNameType: ChoiceEnums.MenuNameType) {
        searchItem.value[menuNameType].folderItem = folder;
        searchItem.value[menuNameType].displayFolderError = (isFolderRequired.value && !searchItem.value[menuNameType].folderItem) ? true : false;
    }

    function onChangeSearchDocumentType(searchResultItem: ISearchDocumentTypeItem) {
        searchItemDocumentType.value = searchResultItem;
    }

    function createNewPatient() {
        formData.createNewPatient = true;
    }

    function onPatientCreated(patientId: number) {
        formData.createNewPatient = false;
        defaultPatientKey.value = {
            patientId: patientId
        }
    }

    function onSelectReviewedBy(selectedResults: IListByNameExtended[]) {
        formData.reviewList = selectedResults;
    }

    function checkSaveInputs() : {identifierId: number, folderMenuNameId: ChoiceEnums.MenuNameType, folderMenuId: number, documentTypeId: number, error: boolean} {
        let blnError = false;
        let identifierId = 0;
        let folderMenuNameId = formData.menuNameId;
        let folderMenuId = 0;
        let documentTypeId = 0;

        identifierId = (ChoiceEnums.MenuNameType.Patient === folderMenuNameId) ? searchItem.value[folderMenuNameId].searchResultItem?.patient_id : searchItem.value[folderMenuNameId].searchResultItem?.entity_id;

        if (identifierId == 0) {
                blnError = true;
                searchItem.value[folderMenuNameId].displaySearchError = true;
            }

        if (searchItemDocumentType.value?.documentTypeId) {
            documentTypeId = searchItemDocumentType.value.documentTypeId;
        }
        else {
            blnError = true;
        }

        if (searchItem.value[folderMenuNameId].folderItem) {
            folderMenuId = searchItem.value[folderMenuNameId].folderItem?.folder_menu_id ?? 0;
        }
        if (folderMenuId == 0 && isFolderRequired) {
            blnError = true;
            searchItem.value[folderMenuNameId].displayFolderError = true;
        }

        // Make sure the filename has been entered
        if (formData.filename?.length == 0) {
            blnError = true;
        }

        return {identifierId: identifierId, folderMenuNameId: folderMenuNameId, folderMenuId: folderMenuId, documentTypeId: documentTypeId, error: blnError};
    }

    async function saveEdocs() {
        const checkSave = checkSaveInputs();

        if (checkSave.error) {
            return;
        } 

        //We are not reconciling these documents real-time, due to performance concerns, we don't want client waiting a long time for everything to process
        //So we will set up a Lambda in AWS with code that takes reconciled documents and moves them into OfficeEMR, then deletes them from Unsolicited DB and S3, and audits.
        //So this save operation is essentially saving their metadata and marking the document as Reconciled, so it can be picked by job (or we could trigger job here).
        const saveFilename = `${formData.filename?.replace(".pdf","")}.pdf`;

         const post_data = <IUnsolicitedDocumentSaveRequest>{
            documentId: props.documentId,
            documentFileName: saveFilename,
            documentComment: selectedDocument.documentInfo?.documentComment,
            officeMenuId: checkSave.folderMenuId,
            documentTypeId: checkSave.documentTypeId,
            identifierType: ChoiceEnums.MenuNameType[checkSave.folderMenuNameId],  
            identifierId: checkSave.identifierId,
            externalId: selectedDocument.documentInfo?.externalId,
            userReconciling: props.authUser,
            isReconciled: true,
            reviewMessage: formData.reviewMessage,
            priority: formData.priority,
            reviewerList: Array()
        }

        for (const item of formData.reviewList) {
            post_data.reviewerList?.push({
                documentReviewerId: undefined,
                documentId: props.documentId,
                reviewerType: +item.optionalIdentifier,
                reviewerId: +item.identifier
            });
        }

        formData.saving = true;
        await documents.updateDocument(post_data, ChoiceEnums.SecurityScreenId.DocumentReconciliation, ChoiceEnums.AppScreenNames.DocumentReconciliation);
        formData.saving = false;

        alert("Document has been reconciled and placed in the queue to be processed shortly.");

        window.close();
    }


</script>

<template> 

    <div class="container-fluid">
        <PageTitle>
            <template v-slot:title>
                {{ pageTitle }}
            </template>
        </PageTitle>
        <hr>

        <div class="row" v-if="formData.isReconciled && !props.viewAudit">
            <div class="col">
                This document has already been reconciled on {{ formData.dateReconciled  }}
            </div>
        </div>

        <div class="row" v-if="!formData.isReconciled && !props.viewAudit">
            <div class="col label-isalus-small">
                <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="menuName" id="menuPatient" :value="ChoiceEnums.MenuNameType.Patient" v-model="formData.menuNameId" checked>
                    <label class="form-check-label label-isalus-small" for="menuName">Patient</label>
                </div>
                <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="menuName" id="menuPractice" :value="ChoiceEnums.MenuNameType.Practice" v-model="formData.menuNameId">
                    <label class="form-check-label label-isalus-small" for="menuName">Practice</label>
                </div>
                <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="menuName" id="menuResource" :value="ChoiceEnums.MenuNameType.Resource" v-model="formData.menuNameId">
                    <label class="form-check-label label-isalus-small" for="menuName">Resource</label>
                </div>
                <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="menuName" id="menuPayer" :value="ChoiceEnums.MenuNameType.Payer" v-model="formData.menuNameId">
                    <label class="form-check-label label-isalus-small" for="menuName">Payer</label>
                </div>
                <div class="row" v-show="displayPatientMenu">
                    <div class="col-auto pe-0 text-danger">*</div>
                    <div class="col pe-1 ps-0">
                        <SearchPatient
                            :defaultSearchKey="defaultPatientKey" 
                            :addToBaseClass="'label-isalus-small'"
                            :showClearSearch="true"
                            @onChange="(val) => onChangeSearch(val, ChoiceEnums.MenuNameType.Patient)">
                        </SearchPatient>
                        <MessageSection v-if="searchItem[ChoiceEnums.MenuNameType.Patient].displaySearchError || !searchItem[ChoiceEnums.MenuNameType.Patient].searchResultItem?.entity_id" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Patient
                        </MessageSection>     
                    </div>
                    <div class="col-auto mt-3 ps-0">
                        <ActionButton
                            baseText = " "
                            spinText = " "
                            :executing="false"    
                            :disabled="!createNewPatientEnabled"   
                            :clickEvent="createNewPatient" 
                            :toolTip="'Create a new patient.'"
                            :executeInit="false"
                            :width="40"
                            :baseClass="'btn btn-primary label-isalus-small'"
                            :overRideStyle="ActionButtonStyle.Add">
                        </ActionButton>
                    </div>
                </div>
                <div class="row" v-show="displayResourceMenu">
                    <div class="col-auto pe-0 text-danger">*</div>
                    <div class="col ps-0">
                        <SearchResource 
                            :addToBaseClass="'label-isalus-small'"
                            :showClearSearch="true"
                            @onChange="(val) => onChangeSearch(val, ChoiceEnums.MenuNameType.Resource)">
                        </SearchResource>
                        <MessageSection v-if="searchItem[ChoiceEnums.MenuNameType.Resource].displaySearchError || !searchItem[ChoiceEnums.MenuNameType.Resource].searchResultItem?.entity_id" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Resource
                        </MessageSection>      
                    </div>
                </div>
                <div class="row" v-show="displayPayerMenu">
                    <div class="col-auto pe-0 text-danger">*</div>
                    <div class="col ps-0">
                        <SearchPayer
                            :addToBaseClass="'label-isalus-small'" 
                            :showClearSearch="true"
                            @onChange="(val) => onChangeSearch(val, ChoiceEnums.MenuNameType.Payer)">
                        </SearchPayer>
                        <MessageSection v-if="searchItem[ChoiceEnums.MenuNameType.Payer].displaySearchError || (isFolderRequired && !searchItem[ChoiceEnums.MenuNameType.Payer].searchResultItem?.entity_id)" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Payer
                        </MessageSection>      
                    </div>
                </div>
                <div class="row mt-2" v-show="displayPracticeMenu">
                    <div class="col-auto pe-0 text-danger" v-if="isFolderRequired">*</div><div class="col-auto pe-0 text-danger" v-else="!isFolderRequired">&nbsp;</div>
                    <div class="col ps-0">
                        <SearchDocumentFolder 
                            :menuNameId="formData.menuNameId" 
                            :addToBaseClass="'label-isalus-small'"
                            :showClearSearch="true"
                            placeholder="Search Practice Folders" 
                            @onChange="(val) => onChangeFolder(val, ChoiceEnums.MenuNameType.Practice)">
                        </SearchDocumentFolder>
                        <MessageSection v-if="isFolderRequired && !searchItem[ChoiceEnums.MenuNameType.Practice].folderItem?.folder_menu_id" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Practice folder
                        </MessageSection>      
                    </div>
                </div>
                <div class="row mt-2" v-show="displayPatientMenu">
                    <div class="col-auto pe-0 text-danger" v-if="isFolderRequired">*</div><div class="col-auto pe-0 text-danger" v-else="!isFolderRequired">&nbsp;</div>
                    <div class="col ps-0">
                        <SearchDocumentFolder 
                            :menuNameId="formData.menuNameId"
                            :defaultSearchKey="defaultDocumentFolderKey" 
                            :addToBaseClass="'label-isalus-small'" 
                            :showClearSearch="true"
                            placeholder="Search Patient Folders" 
                            @onChange="(val) => onChangeFolder(val, ChoiceEnums.MenuNameType.Patient)">
                        </SearchDocumentFolder>
                        <MessageSection v-if="isFolderRequired && !searchItem[ChoiceEnums.MenuNameType.Patient].folderItem?.folder_menu_id" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Patient folder
                        </MessageSection>      
                    </div>
                </div>
                <div class="row mt-2" v-show="displayResourceMenu">
                    <div class="col-auto pe-0 text-danger" v-if="isFolderRequired">*</div><div class="col-auto pe-0 text-danger" v-else="!isFolderRequired">&nbsp;</div>
                    <div class="col ps-0">
                        <SearchDocumentFolder 
                            :menuNameId="formData.menuNameId"
                            :addToBaseClass="'label-isalus-small'" 
                            placeholder="Search Resource Folders" 
                            :showClearSearch="true"
                            @onChange="(val) => onChangeFolder(val, ChoiceEnums.MenuNameType.Resource)">
                        </SearchDocumentFolder>
                        <MessageSection v-if="isFolderRequired && !searchItem[ChoiceEnums.MenuNameType.Resource].folderItem?.folder_menu_id" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Resource folder
                        </MessageSection>      
                    </div>
                </div>
                <div class="row mt-2" v-show="displayPayerMenu">
                    <div class="col-auto pe-0 text-danger" v-if="isFolderRequired">*</div><div class="col-auto pe-0 text-danger" v-else="!isFolderRequired">&nbsp;</div>
                    <div class="col ps-0">
                        <SearchDocumentFolder 
                            :menuNameId="formData.menuNameId"
                            :addToBaseClass="'label-isalus-small'" 
                            placeholder="Search Payer Folders" 
                            :showClearSearch="true"
                            @onChange="(val) => onChangeFolder(val, ChoiceEnums.MenuNameType.Payer)">
                        </SearchDocumentFolder>
                        <MessageSection v-if="isFolderRequired && !searchItem[ChoiceEnums.MenuNameType.Payer].folderItem?.folder_menu_id" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a Payer folder
                        </MessageSection>                        
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col label-isalus-small mt-2">
                        Filename
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <TextInput 
                            v-model:text-value="formData.filename" 
                            :maxLength="96"
                            placeholder="Please enter a filename"
                            :acceptCharacter="Validation.LimitToFilenameCharacters"
                            :cssFramework = "'vanilla'">
                            <template #RightAddon>
                                .pdf
                            </template>
                        </TextInput>
                        <MessageSection v-if="displayFilenameError" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please enter a filename
                        </MessageSection>      
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col label-isalus-small">
                        Received Date
                    </div>
                </div>
                <div class="row">
                    <div class="date-isalus-small">
                        <DatePickerNoTZ v-model:value="formData.receivedDate" :cssFramework="'vanilla'" :disabled="true" :clearable="false" />
                    </div>
                </div>
                
            </div>
            <div class="col">
                <div class="row">
                    <div class="col label-isalus-small">
                        <SearchUserGroupMultiple 
                            label="To Be Reviewed By" 
                            :addDefaultList="formData.reviewList"
                            :addToBaseClass="'label-isalus-small'" 
                            @onSelected="onSelectReviewedBy">
                        </SearchUserGroupMultiple>
                        <MessageSection v-if="isReviewRequired && formData.reviewList.length < 1" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                            Please select a reviewer
                        </MessageSection>
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col label-isalus-small">
                        Review Message
                    </div> 
                </div>
                <div class="row">
                    <div class="col">
                        <TextInput 
                            v-model:text-value="formData.reviewMessage" 
                            :maxLength="1500"
                            :cssFramework = "'vanilla'"
                            :rows=3>
                        </TextInput>
                    </div>
                </div> 
                
                <div class="row mt-2">
                    <div class="col label-isalus-small">
                        <div class="row mt-2">
                            <div class="col-auto pe-0 text-danger" v-if="isDocumentTypeRequired">*</div>
                            <div class="col ps-0">
                                <SearchDocumentType 
                                    :addToBaseClass="'label-isalus-small'" 
                                    :defaultSearchKey="defaultDocumentTypeKey" 
                                    placeholder="Search Document Types" 
                                    :showClearSearch="true"
                                    @onChange="(val) => onChangeSearchDocumentType(val)">
                                </SearchDocumentType>
                                <MessageSection v-if="isDocumentTypeRequired && !searchItemDocumentType" :show-icon="false" :type="UIEnums.MessageSectionType.Text" :display-style="UIEnums.DisplayStyle.Danger">
                                    Please enter a document type
                                </MessageSection>         
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col label-isalus-small">
                        Priority
                    </div>
                    <div class="row">
                        <div class="col">
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="priority" id="priorityLow" :value="ChoiceEnums.Priority.Low" v-model="formData.priority">
                                <label class="form-check-label label-isalus-small" for="priority">Low</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="priority" id="priorityNormal" :value="ChoiceEnums.Priority.Normal" v-model="formData.priority">
                                <label class="form-check-label label-isalus-small" for="priority">Normal</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="priority" id="priorityHigh" :value="ChoiceEnums.Priority.High" v-model="formData.priority">
                                <label class="form-check-label label-isalus-small" for="priority">High</label>
                            </div>
                        </div>
                    </div>                    
                </div>
            </div>
        </div>            
    </div>
    
    <div v-if="formData.errorMessage" class="text-danger pt-1 pb-2 text-center">{{formData.errorMessage}}</div>
    <div v-if="1 !== props.canUpdate" class="text-danger pt-1 pb-2 text-center">You do not have sufficient privileges to perform a Save.</div>
    <div v-if="!formData.isReconciled && !props.viewAudit" class="text-end pe-2">
        <hr>
        <ActionButton
            baseText = "Save"
            spinText = "Saving"
            :executing="formData.saving"    
            :disabled="disableSaveButton"   
            :clickEvent="saveEdocs" 
            :toolTip="'Save the PDF to eDocuments'"
            :executeInit="false"
            :width="120">
        </ActionButton> 
    </div>

    <div v-if="props.viewAudit">
        <div class="row pt-2">
            <UnsolicitedDocumentAudit v-bind:auditData="selectedDocumentAudit.auditData"/>
        </div>
    </div>

    <ModalDialog v-model:show="formData.createNewPatient" :closeOnClickOutside=false :size="UIEnums.ModalSize.Medium">
        <template #title>Create New Patient</template>
        <PatientCreateQuick 
            :saveEnabled="1"
            :resetFields="formData.createNewPatient"
            @onPatientCreated="(val) => onPatientCreated(val)">
        </PatientCreateQuick>
    </ModalDialog>

</template>